/* eslint-disable eqeqeq */
import { useSelector } from "react-redux";
import { useDispatch } from "react-redux";
import { updateOrderData } from "../../../../store/slices/Orders/addOrderSlice.js";
import { useGetOrdersBasicDataQuery } from "../../../../store/queries/Orders/index.js";
import { useFormik } from "formik";
import Collection from "../AddOrder/Collection/index.js";
import Item from "../AddOrder/Item/index.js";
import Service from "./Service/index.js";
import * as Yup from "yup";
import { useEffect, useState } from "react";
import { createOldCertificate, getAgentData } from "../api.js";
import { toast } from "react-toastify";
import { updateConfig } from "../../../../store/slices/Orders/orderSlice.js";

/**
 * Convert every value in the payload to a string, recursing into nested
 * objects/arrays. null/undefined are left untouched so empty fields are
 * still dropped from the JSON body.
 */
const stringifyDeep = (value) => {
  if (value === null || value === undefined) {
    return value;
  }
  if (Array.isArray(value)) {
    return value.map(stringifyDeep);
  }
  if (typeof value === "object") {
    return Object.entries(value).reduce(
      (acc, [key, val]) => ({ ...acc, [key]: stringifyDeep(val) }),
      {}
    );
  }
  return String(value);
};

/**
 * Old Certificate stock creation form.
 *
 * Mirrors the standard "Add Order" form (`useAddOrder`) but without the
 * Engraving step, and submits a JSON body to `/stock-receipt/create-old-certificate`.
 * The Collection and Item tabs are reused from the AddOrder folder since their
 * fields are identical; only the Service tab is local (different footer button).
 */
const useAddOldCertificate = ({ refetch }) => {
  const [validationType, setValidationType] = useState("");
  const [moveToTab, setMoveToTab] = useState(false);
  const userDetails = JSON?.parse(localStorage?.getItem("user_details"));
  const { data: basicData = {}, isLoading } = useGetOrdersBasicDataQuery();
  const isShopAndSupplier = userDetails?.user_type === "Shop_And_Supplier";
  const PhysicalValue = basicData?.data?.source_list
    ? basicData?.data?.source_list?.find(
        (item) => item.source_name === "PHYSICAL"
      )
    : {
        _id: "6576aae733c7c2b785080606",
        source_name: "PHYSICAL",
      };

  const [initialData, setInitialData] = useState({
    branch_id: "",
    source: isShopAndSupplier ? PhysicalValue?._id : "",
    supplier: "",
    collection_agent: "",
    no_stone: "",
    is_job: "",
    add_prefix: "",
    phone_number: "",
    address: "",
    shop_name: "",
    bill_to: "",
    priority: "",
    item_type: "",
    group: [
      {
        item_id: "",
        total_certificate: "",
        item_quantity: "",
        total_weight: "",
      },
    ],
    total_item: "",
    total_certificate: "",
    service_type: "",
    color: "",
    clarity: "",
    cut: "",
    finish: "",
    double_grade: [],
    packaging_type: "",
    payment_mode: "",
    certificate_type: "",
    prepared_by: "",
    remarks: "",
    box_weight: "",
    due_date: "",
    due_time: "",
    certificate_archive_enabled: "",
  });
  const dispatch = useDispatch();

  const { activeTab, isEditOrder, completedTabs } = useSelector(
    (state) => state.addOrder
  );

  const { orderData, isEdit } = useSelector((state) => state.order);

  // Pre-fill the form when editing an existing old certificate. The edit data
  // is fetched (by `useOrder`) through the same `/stock-receipt/edit` API the
  // standard order edit uses, and stored on `state.order.orderData`.
  useEffect(() => {
    if (orderData && isEdit) {
      let newObj = {
        value: orderData?.delivery_details?.person,
        label: `${orderData?.delivery_details?.person} (${orderData?.delivery_details?.contact_number})`,
      };
      setInitialData({
        branch_id: orderData?.branch_id,
        source: isShopAndSupplier ? PhysicalValue?._id : orderData?.source_id,
        supplier: orderData?.supplier_id,
        shop_name: orderData?.shop_id,
        bill_to: orderData?.billing_address_id,
        priority: orderData?.priority,
        item_type: orderData?.item_type_id,
        group: orderData?.item,
        color: orderData?.color_id,
        clarity: orderData?.clarity_id,
        cut: orderData?.shape_id,
        finish: orderData?.finish_id,
        remarks: orderData?.remarks,
        packaging_type: orderData?.packing_type_id,
        payment_mode: orderData?.payment_mode,
        certificate_type: orderData?.certificate_type,
        collection_agent: newObj,
        phone_number: orderData?.delivery_details?.contact_number,
        address: orderData?.delivery_details?.address,
        prepared_by: orderData?.prepared_by,
        box_weight: orderData?.box_weight,
        service_type: orderData?.service,
        is_job: orderData?.is_job_work,
        add_prefix: orderData?.is_job_work_prefix,
        no_stone: orderData?.no_changeable_stone,
        due_date: orderData?.due_date,
        due_time: orderData?.due_time,
        double_grade: orderData?.grade?.map((val) => val?.id),
        certificate_archive_enabled: orderData?.certificate_archive_enabled,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [orderData, isLoading, basicData, isEdit]);

  const handleCloseAddOrderModal = () => {
    dispatch(updateOrderData((state) => (state.orderStatus = "success")));
    dispatch(
      updateConfig((state) => {
        state.showAddOrderModal = false;
        state.isEdit = false;
        state.isOldCertificate = false;
        state.clearSelection = true;
      })
    );
  };

  const validation = Yup.object({
    branch_id:
      basicData?.data?.branch_list?.length &&
      Yup.string().required("Select Branch"),
    source: Yup.string().required("Select source"),
    supplier: Yup.string().required("Select supplier"),
    collection_agent: Yup.object().required("Enter Name"),
    phone_number: Yup.string()
      .required("Enter Phone number")
      .max(11, "Enter valid number")
      .min(8, "Enter valid number")
      .matches(/^[0-9]*$/, "Enter valid number"),
    address: Yup.string()
      .trim()
      .required("Enter Address")
      .min(5, "Not a valid address"),
    shop_name: Yup.string().required("Select Shop"),
    bill_to: Yup.string().required("Select Billing Shop"),
    priority: Yup.string().required("Select Priority"),
    no_stone: Yup.string().matches(/^[0-9]*$/, "Enter Valid Number"),
    add_prefix: Yup.string().when("is_job", {
      is: (value) => value === 1,
      then: (schema) => schema.required("Select Add Prefix"),
      otherwise: (schema) => schema,
    }),
    item_type: Yup.string().required("Select Item type"),
    service_type: Yup.array()
      .required("Select Service types")
      .min(1, "Select Service types"),
    color:
      (validationType === 1 || validationType === 3) &&
      Yup.string().required("Select Color"),
    clarity:
      (validationType === 1 || validationType === 3) &&
      Yup.string().required("Select Clarity"),
    finish:
      (validationType === 1 || validationType === 3) &&
      Yup.string().required("Select Finish"),
    double_grade:
      (validationType === 2 || validationType === 3 || validationType === 5) &&
      Yup.array().required("Select Grade").min(1, "Select Grade"),
    remarks: Yup.string().required("Enter Remarks"),
    payment_mode: Yup.string().required("Select Payment"),
    prepared_by: Yup.string().required("Select Name"),
    box_weight: Yup.string()
      .max(15, "Invalid weight")
      .matches(/^(?!0(\.0+)?$)\d*(\.\d+)?$/, "Invalid Weight ")
      .nullable(),
    group: Yup.array().of(
      Yup.object().shape({
        item_id: Yup.string().required("Item is required"),
        total_certificate: Yup.string()
          .required("No of certificate is required")
          .matches(/^[1-9][0-9]*$/, "Invalid Number "),
        item_quantity: Yup.string()
          .matches(/^[1-9][0-9]*$/, "Invalid Number ")
          .nullable(),
        total_weight: Yup.string()
          .max(10, "Invalid weight")
          .matches(/^(?!0$)([0-9]*\.[0-9]+|[1-9][0-9]*)$/, "Invalid Number ")
          .nullable(),
      })
    ),
  });

  const formik = useFormik({
    initialValues: initialData,
    validationSchema: validation,
    enableReinitialize: true,
    onSubmit: (values) => {
      const name = values?.collection_agent?.label?.split(" (")[0];
      dispatch(updateOrderData((state) => (state.orderStatus = "pending")));

      let oldCertData = {
        delivery_person: name,
        delivery_person_number: values?.phone_number,
        delivery_person_address: values?.address,
        source_id: isShopAndSupplier ? PhysicalValue?._id : values?.source,
        branch_id:
          basicData?.data?.branch_list?.length !== 0 ? values?.branch_id : "",
        supplier_id: values?.supplier,
        shop_id: values?.shop_name,
        priority: values?.priority,
        no_changeable_stone: values?.no_stone,
        is_job_work: values?.is_job,
        is_job_work_prefix: values?.is_job == 1 ? values?.add_prefix : "",
        item_type_id: values?.item_type,
        billing_address_id: values?.bill_to,
        packing_type_id: values?.packaging_type,
        payment_mode: values?.payment_mode,
        certificate_type: values?.certificate_type,
        prepared_by: values?.prepared_by,
        remarks: values?.remarks,
        box_weight: values?.box_weight,
        due_date: values?.due_date,
        due_time: values?.due_time,
        certificate_archive_enabled: basicData?.data?.shop_list?.find(
          (shop) => shop?._id === values?.shop_name
        )?.is_certificate_archive_enabled
          ? values?.certificate_archive_enabled
          : 0,
      };

      // keep only non-empty scalar fields, mirroring the standard payload filter
      oldCertData = Object.entries(oldCertData)
        .filter(
          (item) => item[1] !== undefined && item[1] !== "" && item[1] !== null
        )
        .reduce((a, v) => ({ ...a, [v[0]]: v[1] }), {});

      const payload = stringifyDeep({
        ...oldCertData,
        // On edit, the same create API performs the update — it just needs the id.
        ...(isEdit ? { stock_receipt_id: orderData?._id } : {}),
        is_engraving: 2,
        item_grading: {
          color_id: values?.color,
          clarity_id: values?.clarity,
          shape_id: values?.cut,
          finish_id: values?.finish,
        },
        service_id: values?.service_type?.map((value) => value?._id) ?? [],
        grade: values?.double_grade ?? [],
        item:
          values?.group?.map((value) => ({
            item_id: value?.item_id,
            total_certificate: value?.total_certificate,
            item_quantity: value?.item_quantity,
            total_weight: value?.total_weight,
          })) ?? [],
      });

      createOldCertificate(payload).then((response) => {
        if (response?.data?.status_code === 200) {
          dispatch(updateOrderData((state) => (state.orderStatus = "success")));
          toast.success(
            response?.data?.message ||
              (isEdit
                ? "Old Certificate Updated successfully"
                : "Old Certificate Created successfully")
          );
          handleCloseAddOrderModal();
          refetch();
        } else if (response?.status_code === 422) {
          let errors = response?.errors;
          let errorFields = Object.keys(errors);
          errorFields.forEach((field) => {
            formik.setFieldError(field, errors[field]);
          });
          dispatch(updateOrderData((state) => (state.orderStatus = "failed")));
        } else {
          toast.error("Something went wrong");
          dispatch(updateOrderData((state) => (state.orderStatus = "failed")));
        }
      });
    },
  });

  const numberOfItem = formik?.values?.group?.reduce(
    (acc, item) => acc + +item?.item_quantity,
    0
  );
  const numberOfCertificate = formik?.values?.group?.reduce(
    (acc, item) => acc + +item?.total_certificate,
    0
  );

  useEffect(() => {
    if (basicData?.data?.prepared_by) {
      formik?.setFieldValue(
        "prepared_by",
        basicData?.data?.prepared_by?.[0]?._id
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [basicData, isLoading]);

  useEffect(() => {
    if (!isEdit) {
      if (
        formik?.values?.phone_number?.length > 8 &&
        formik?.values?.phone_number?.length < 15
      ) {
        getAgentData(formik?.values?.phone_number).then((response) => {
          if (response?.data?.status_code === 200) {
            if (response?.data?.data?.length !== 0) {
              formik?.setFieldValue(
                "address",
                response?.data?.data?.[0]?.delivery_details?.address
              );

              let newObj = {
                value: response?.data?.data?.[0]?._id,
                label: `${response?.data?.data?.[0]?.delivery_details?.person} (${response?.data?.data?.[0]?.delivery_details?.contact_number})`,
              };

              formik?.setFieldValue("collection_agent", newObj);
            }
          }
        });
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik?.values?.phone_number, isEdit]);

  const [collectionAgentList, setCollectionAgentList] = useState([]);

  const tabs = [
    {
      label: "Collection",
      completed: calculateCompletionStatus([
        "branch_id",
        "source",
        "supplier",
        "phone_number",
        "collection_agent",
        "address",
        "shop_name",
        "bill_to",
        "priority",
        "no_stone",
        "is_job",
        "add_prefix",
        "due_date",
        "due_time",
        "certificate_archive_enabled",
      ]),
    },
    {
      label: "Item",
      completed: calculateCompletionStatus([
        "item_type",
        "box_weight",
        "item_id",
        "total_certificate",
        "item_quantity",
        "total_weight",
      ]),
    },
    {
      label: "Service",
      completed: calculateCompletionStatus([
        "service_type",
        "color",
        "clarity",
        "cut",
        "finish",
        "double_grade",
        "packaging_type",
        "certificate_type",
        "prepared_by",
        "payment_mode",
        "remarks",
      ]),
    },
  ];

  const tabsAndFields = [
    {
      label: "Collection",
      fields: [
        "branch_id",
        "source",
        "supplier",
        "phone_number",
        "collection_agent",
        "address",
        "shop_name",
        "bill_to",
        "priority",
        "no_stone",
        "is_job",
        "add_prefix",
        "due_date",
        "due_time",
        "certificate_archive_enabled",
      ],
    },
    {
      label: "Item",
      fields: [
        "item_type",
        "box_weight",
        "item_id",
        "total_certificate",
        "item_quantity",
        "total_weight",
      ],
    },
    {
      label: "Service",
      fields: [
        "service_type",
        "color",
        "clarity",
        "cut",
        "finish",
        "double_grade",
        "packaging_type",
        "certificate_type",
        "prepared_by",
        "payment_mode",
        "remarks",
      ],
    },
  ];

  useEffect(() => {
    if (moveToTab === true) {
      handleMoveToErrorTab();
    }
    setMoveToTab(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [formik?.errors, moveToTab]);

  function calculateCompletionStatus(fields) {
    // eslint-disable-next-line array-callback-return
    var completedFields = fields?.filter((field) => {
      if (Array.isArray(formik?.values[field])) {
        return formik?.values[field]?.length > 0;
      } else if (
        field === "item_id" ||
        field === "total_certificate" ||
        field === "item_quantity" ||
        field === "item_weight"
      ) {
        return !!formik?.values?.group?.[0]?.[field];
      } else {
        return !!formik?.values[field];
      }
    });
    return (completedFields.length / fields.length) * 130;
  }

  const handleTabClick = (tab) => {
    if (completedTabs[tab?.label] === true) {
      dispatch(
        updateOrderData((state) => {
          state.activeTab = tab?.label;
        })
      );
    } else if (isEditOrder) {
      dispatch(
        updateOrderData((state) => {
          state.activeTab = tab?.label;
        })
      );
    }
  };

  const getFieldError = (fieldName) => {
    if (formik.touched[fieldName] && formik.errors[fieldName]) {
      return formik.errors[fieldName];
    }
    return "";
  };

  const handleSubmit = () => {
    formik.setTouched(
      Object.keys(formik.values).reduce((acc, key) => {
        acc[key] = true;
        return acc;
      }, {})
    );
    formik.setFieldTouched(`group[${0}].item_id`, true);
    formik.setFieldTouched(`group[${0}].item_quantity`, true);
    formik.setFieldTouched(`group[${0}].total_certificate`, true);
    formik.setFieldTouched(`group[${0}].total_weight`, true);
    formik?.setFieldTouched("item_type", true);
    formik?.setFieldTouched("address", true);
    handleMoveToErrorTab();
  };

  const renderTabContent = () => {
    switch (activeTab) {
      case "Collection":
        return (
          <Collection
            formik={formik}
            tabs={tabs}
            optionsData={basicData?.data}
            getFieldError={getFieldError}
            collectionAgentList={collectionAgentList}
            setCollectionAgentList={setCollectionAgentList}
            PhysicalValue={PhysicalValue}
            isShopAndSupplier={isShopAndSupplier}
          />
        );
      case "Item":
        return (
          <Item
            tabs={tabs}
            isEdit={isEdit}
            formik={formik}
            optionsData={basicData?.data}
            getFieldError={getFieldError}
            setValidationType={setValidationType}
            numberOfCertificate={numberOfCertificate}
            numberOfItem={numberOfItem}
          />
        );
      case "Service":
        return (
          <Service
            formik={formik}
            optionsData={basicData?.data}
            getFieldError={getFieldError}
            tabs={tabs}
            handleSubmit={handleSubmit}
            isEdit={isEdit}
          />
        );
      default:
        return null;
    }
  };

  function setActiveTabByName(active) {
    setMoveToTab(false);
    const updatedTab = tabs.find((tab) => tab?.label === active);
    dispatch(
      updateOrderData((state) => {
        state.activeTab = updatedTab?.label;
      })
    );
  }

  const handleMoveToErrorTab = () => {
    // Move activeTab to the first tab with errors
    const firstErrorTab = Object.keys(formik.errors)[0];
    if (firstErrorTab === "group") {
      setActiveTabByName("Item");
      return "";
    }
    if (firstErrorTab && firstErrorTab !== activeTab) {
      const errorTab = tabsAndFields?.find((tab) =>
        tab.fields.includes(firstErrorTab)
      );
      setActiveTabByName(errorTab?.label);
    }
    setMoveToTab(true);
  };

  return {
    tabs,
    formik,
    basicData: basicData?.data,
    activeTab,
    isEditOrder,
    completedTabs,
    getFieldError,
    renderTabContent,
    handleTabClick,
    numberOfCertificate,
    numberOfItem,
  };
};

export default useAddOldCertificate;
